import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "portfolio-risk-tab" }
const _hoisted_2 = { class: "portfolio-risk-tab__toolbar" }
const _hoisted_3 = { class: "portfolio-risk-tab__toolbar-right" }
const _hoisted_4 = { class: "portfolio-risk-tab__chart-area" }
const _hoisted_5 = {
  key: 0,
  class: "portfolio-risk-tab__loading"
}
const _hoisted_6 = {
  key: 1,
  class: "portfolio-risk-tab__empty"
}

import { computed } from 'vue';
import Chart from 'primevue/chart';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import ToolbarIconButton from '@/components/ui/ToolbarIconButton.vue';
import IconExport from '@/components/icons/IconExport.vue';
import QueuedExportModal from '@/components/modals/QueuedExportModal.vue';
import { useReportsPortfolioRisk } from '@/composables/useReportsPortfolioRisk';
import { externalTooltip } from '@/utils/chartTooltip';
import { formatDate } from '@/utils/formatUtils';
import { exportPortfolioRiskReport } from '@/services/reportsService';
import { useQueuedExport } from '@/composables/useQueuedExport';
import { useDateRangeWithTimezone } from '@/composables/useDateRangeWithTimezone';

const CANVAS_W = 860;
const CANVAS_H = 580;


export default /*@__PURE__*/_defineComponent({
  __name: 'ReportsPortfolioRiskTab',
  setup(__props) {

/* eslint-env vue/setup-compiler-macros */
/* eslint-disable @typescript-eslint/no-explicit-any */
const { selectedDateRange } = useDateRangeWithTimezone();

const formattedDateRange = computed<[string, string]>(() => [
    formatDate(selectedDateRange.value?.[0] ?? new Date()),
    formatDate(selectedDateRange.value?.[1] ?? new Date()),
]);

const {
    chartData,
    percentages,
    loading,
    total,
    openCategory,
} = useReportsPortfolioRisk(formattedDateRange);

const onSliceSelect = ({ element }: { element: { index: number } }) => {
    openCategory(element.index);
};

const chartKey = computed(() => percentages.value.map((p) => p.percent).join('-'));

const {
    exporting,
    cooldownActive,
    queuedModalVisible,
    queuedTitle,
    queuedMessage,
    run: runExport,
} = useQueuedExport({
    queuedTitle: 'Portfolio risk export started',
    successDetail: 'Portfolio risk CSV downloaded.',
    errorDetail: 'Unable to export the portfolio risk report. Please try again.',
});

function exportCsv() {
    const [from, to] = formattedDateRange.value;

    runExport(() => exportPortfolioRiskReport({ from, to }));
}

const outerLabelsPlugin = [{
    id: 'outerLabels',
    afterDraw(chart: any) {
        const { ctx } = chart as { ctx: CanvasRenderingContext2D };
        const meta = chart.getDatasetMeta(0);
        if (!meta?.data?.length) return;

        const vals = (chart.data.datasets?.[0]?.data ?? []) as number[];
        const lbls = (chart.data.labels ?? []) as string[];
        if (!vals.length) return;

        const hasData = vals.some((v) => v > 0);
        if (!hasData) return;

        const GAP = 52; // px between arc edge and label anchor
        const PCT_FONT = '600 32px Archivo, sans-serif';
        const DESC_FONT = '400 14px Archivo, sans-serif';
        const MAX_DESC_W = 150;
        const LINE_H = 19;

        const wrap = (text: string, maxW: number): string[] => {
            ctx.font = DESC_FONT;
            const words = text.split(' ');
            const lines: string[] = [];
            let cur = '';
            for (const w of words) {
                const attempt = cur ? `${cur} ${w}` : w;
                if (ctx.measureText(attempt).width > maxW && cur) {
                    lines.push(cur);
                    cur = w;
                } else {
                    cur = attempt;
                }
            }
            if (cur) lines.push(cur);
            return lines;
        };

        meta.data.forEach((arc: any, i: number) => {
            if (!vals[i]) return;

            const {
                startAngle, endAngle, x: cx, y: cy, outerRadius,
            } = arc.getProps(['startAngle', 'endAngle', 'x', 'y', 'outerRadius'], true) as {
                startAngle: number; endAngle: number;
                x: number; y: number; outerRadius: number;
            };

            // Skip label if arc is too small to label without overlap (< 5° arc)
            if (endAngle - startAngle < 0.087) return;

            const mid = (startAngle + endAngle) / 2;
            const cosA = Math.cos(mid);
            const sinA = Math.sin(mid);

            // Anchor point just outside the arc
            const ax = cx + cosA * (outerRadius + GAP);
            const ay = cy + sinA * (outerRadius + GAP);

            const pct = `${percentages.value[i]?.percent ?? '0'}%`;
            const descLines = wrap(lbls[i] ?? '', MAX_DESC_W);

            // Text-align based on horizontal position of anchor
            let align: CanvasTextAlign;
            if (cosA < -0.2) align = 'right';
            else if (cosA > 0.2) align = 'left';
            else align = 'center';

            ctx.save();
            ctx.textAlign = align;

            // Percentage — large bold
            ctx.font = PCT_FONT;
            ctx.fillStyle = '#111111';
            ctx.textBaseline = 'bottom'; // pct sits just above anchor
            ctx.fillText(pct, ax, ay);

            // Description — smaller, below anchor
            ctx.font = DESC_FONT;
            ctx.fillStyle = '#111111';
            ctx.textBaseline = 'top';
            descLines.forEach((line, li) => {
                ctx.fillText(line, ax, ay + 4 + li * LINE_H);
            });

            ctx.restore();
        });
    },
}];

const chartOptions = {
    responsive: false,
    cutout: '45%',
    layout: {
        padding: {
            top: 90,
            bottom: 100,
            left: 200,
            right: 200,
        },
    },
    onHover: (event: any, elements: unknown[]) => {
        const canvas = event?.native?.target as HTMLElement | undefined;
        if (canvas) canvas.style.cursor = elements.length ? 'pointer' : 'default';
    },
    plugins: {
        legend: { display: false },
        tooltip: {
            ...externalTooltip({
                title: (items: Array<{ label: string }>) => items[0]?.label ?? '',
                label: (ctx: { dataIndex: number; parsed: number }) => {
                    const count = ctx.parsed ?? 0;
                    const percent = percentages.value[ctx.dataIndex]?.percent ?? '0';

                    return `${count.toLocaleString('en-US')} ${count === 1 ? 'transaction' : 'transactions'} (${percent}%)`;
                },
            }),

            position: 'average' as const,
        },
    },
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(DateRangePicker, {
          modelValue: _unref(selectedDateRange),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(selectedDateRange) ? (selectedDateRange).value = $event : null)),
          label: "Filter by Date:",
          "trigger-width": "350px",
          "trigger-radius": "10px"
        }, null, 8, ["modelValue"]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(ToolbarIconButton, {
            ariaLabel: "Export",
            disabled: _unref(exporting) || _unref(cooldownActive),
            onClick: exportCsv
          }, {
            default: _withCtx(() => [
              _createVNode(IconExport)
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ]),
      _createElementVNode("div", _hoisted_4, [
        (_unref(loading))
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("div", { class: "portfolio-risk-tab__loading-ring" }, null, -1)
            ]))]))
          : (_unref(total) === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[3] || (_cache[3] = [
                _createElementVNode("p", { class: "portfolio-risk-tab__empty-title" }, "No data available", -1),
                _createElementVNode("p", { class: "portfolio-risk-tab__empty-text" }, "There is no portfolio risk data for the selected date range.", -1)
              ]))]))
            : (_openBlock(), _createBlock(_unref(Chart), {
                key: chartKey.value,
                type: "doughnut",
                data: _unref(chartData),
                options: chartOptions,
                plugins: outerLabelsPlugin,
                width: CANVAS_W,
                height: CANVAS_H,
                onSelect: onSliceSelect
              }, null, 8, ["data"]))
      ])
    ]),
    _createVNode(QueuedExportModal, {
      visible: _unref(queuedModalVisible),
      "onUpdate:visible": _cache[1] || (_cache[1] = ($event: any) => (_isRef(queuedModalVisible) ? (queuedModalVisible).value = $event : null)),
      title: _unref(queuedTitle),
      message: _unref(queuedMessage)
    }, null, 8, ["visible", "title", "message"])
  ], 64))
}
}

})